import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponseDocumentsService, CreateDocumentRequestBody } from './api/response-documents/api-response-documents.service';
import { DocumentScope } from '../models/response-document-subtype';
import { isArchiveFile, ResponseProjectUploadFile } from '../models/response-project-document';
import { ResponseProjectProcessingPayload } from '../models/response-project';

export interface DocumentImportContext {
  projectId: string | null;
  scope: DocumentScope;
  defaultSubtypeId?: number;
}

export type DocumentImportErrorReason = 'not-found' | 'already-exists' | 'upload-error';

export type DocumentImportEvent =
  | { type: 'created'; documentId: number; uid: string }
  | { type: 'progress'; progress: number }
  | { type: 'uploaded' }
  | { type: 'error'; stage: 'create' | 'upload'; reason: DocumentImportErrorReason };

@Injectable({ providedIn: 'root' })
export class DocumentImportService {
  constructor(private apiDocuments: ApiResponseDocumentsService) {}

  isArchive(file: ResponseProjectUploadFile): boolean {
    return isArchiveFile(file.name);
  }

  splitArchives(files: ResponseProjectUploadFile[]): {
    archives: ResponseProjectUploadFile[];
    regular: ResponseProjectUploadFile[];
  } {
    return {
      archives: files.filter(f => this.isArchive(f)),
      regular: files.filter(f => !this.isArchive(f)),
    };
  }

  buildProcessingPayload(ctx: DocumentImportContext, files: ResponseProjectUploadFile[]): ResponseProjectProcessingPayload {
    const payload: ResponseProjectProcessingPayload = {
      user_token: localStorage.getItem('api_token') ?? '',
      scope: ctx.scope,
      files: files.map(f => ({ name: f.name, uid: f.uid, id: f.documentId! })),
    };
    if (ctx.projectId) {
      payload.project_id = Number(ctx.projectId);
    }
    return payload;
  }

  /**
   * Creates the document record then uploads its content to the presigned S3 URL,
   * emitting granular progress events. For non-archive files it also flags the
   * record as `uploaded`. When `options.replaceId` is set the existing document is
   * replaced in place.
   */
  uploadFile(
    ctx: DocumentImportContext,
    file: ResponseProjectUploadFile,
    options?: { replaceId?: number }
  ): Observable<DocumentImportEvent> {
    return new Observable<DocumentImportEvent>(subscriber => {
      const source = file.sourceFile;
      if (!(source instanceof File)) {
        subscriber.complete();
        return;
      }

      const isArchive = isArchiveFile(source.name);
      const createBody: CreateDocumentRequestBody = {
        file_size: source.size,
        name: source.name,
        response_document_subtype_id: ctx.defaultSubtypeId,
        is_in_library: !ctx.projectId && !isArchive,
      };
      if (ctx.projectId) createBody.response_project_id = Number(ctx.projectId);
      if (options?.replaceId != null) createBody.id = options.replaceId;

      let uploadSub: { unsubscribe(): void } | null = null;

      const createSub = this.apiDocuments.createDocument(createBody).subscribe({
        next: res => {
          subscriber.next({ type: 'created', documentId: res.id, uid: res.uid });

          uploadSub = this.apiDocuments.uploadToPresignedUrl(res.presigned_url, source, res.content_type).subscribe({
            next: progress => subscriber.next({ type: 'progress', progress }),
            error: () => {
              subscriber.next({ type: 'error', stage: 'upload', reason: 'upload-error' });
              subscriber.complete();
            },
            complete: () => {
              if (!isArchive) {
                this.apiDocuments.updateDocument({ id: res.id, status: 'uploaded' }).subscribe({ error: () => undefined });
              }
              subscriber.next({ type: 'uploaded' });
              subscriber.complete();
            },
          });
        },
        error: (err: { status?: number }) => {
          const reason: DocumentImportErrorReason =
            err.status === 404 ? 'not-found' : err.status === 409 ? 'already-exists' : 'upload-error';
          subscriber.next({ type: 'error', stage: 'create', reason });
          subscriber.complete();
        },
      });

      return () => {
        createSub.unsubscribe();
        uploadSub?.unsubscribe();
      };
    });
  }

  async runWithConcurrency<T>(items: T[], concurrency: number, fn: (item: T) => Promise<void>): Promise<void> {
    if (!items.length) return;
    const executing = new Set<Promise<void>>();
    for (const item of items) {
      const p = fn(item).finally(() => executing.delete(p));
      executing.add(p);
      if (executing.size >= concurrency) await Promise.race(executing);
    }
    await Promise.allSettled([...executing]);
  }
}
